import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col, Card, Table, Badge, Button, Modal, Form } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import API from '../services/api';
import { Kpi, ChartCard, COLORS, dateTime, cleanIp } from './adminShared';
import AdminEmergencyPanel from './AdminEmergencyPanel';
import Loading from './Loading';

const AdminSecurityTab = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [target, setTarget] = useState(null); // { ip, usernames } being blocked
  const [reason, setReason] = useState('');
  const [known, setKnown] = useState(null); // usernames that have signed in from that IP
  const [blockError, setBlockError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => API.get('/admin/security').then(r => { setData(r.data); setError(''); })
    .catch(err => setError(err.response?.data?.message || 'No se pudo cargar la seguridad.')), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const openBlock = (ip, usernames = []) => { setTarget({ ip: cleanIp(ip), usernames }); setReason(usernames.length ? `Intentos fallidos con: ${usernames.join(', ')}`.slice(0, 200) : ''); setKnown(null); setBlockError(''); };
  const closeBlock = () => setTarget(null);

  const block = async (force = false) => {
    setBusy(true);
    setBlockError('');
    try {
      await API.post('/admin/block-ip', { ip: target.ip, reason, force });
      closeBlock();
      load();
    } catch (err) {
      if (err.response?.data?.code === 'KNOWN_IP') setKnown(err.response.data.knownUsers);
      else setBlockError(err.response?.data?.message || 'No se pudo bloquear la IP.');
    } finally {
      setBusy(false);
    }
  };

  const unblock = async (ip) => {
    if (!window.confirm(`¿Desbloquear ${ip}? Podrá volver a intentar iniciar sesión.`)) return;
    try {
      await API.post('/admin/unblock-ip', { ip });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo desbloquear la IP.');
    }
  };

  const BlockButton = ({ ip, usernames, blocked }) => (blocked
    ? <Badge bg="danger">Bloqueada</Badge>
    : <Button size="sm" variant="outline-danger" onClick={() => openBlock(ip, usernames)}><i className="bi bi-slash-circle me-1" />Bloquear</Button>);

  if (error && !data) return <div className="alert alert-danger">{error}</div>;
  if (!data) return <Loading label="Cargando seguridad..." />;

  const { last24h: d, accountsToReview: a } = data;
  const daily = {
    labels: data.dailyActivity.map(x => `${x.day.slice(8, 10)}/${x.day.slice(5, 7)}`),
    datasets: [{ data: data.dailyActivity.map(x => x.actions), backgroundColor: COLORS.cyan, borderRadius: 4 }],
  };

  return (
    <>
      <AdminEmergencyPanel />
      <Row className="g-3 mb-3">
        <Col md={2} sm={4} xs={6}><Kpi label="Inicios de sesión (24 h)" value={d.logins} /></Col>
        <Col md={2} sm={4} xs={6}><Kpi label="Intentos fallidos (24 h)" value={d.failedLogins} tone={d.failedLogins > 0 ? 'amber' : undefined} /></Col>
        <Col md={2} sm={4} xs={6}><Kpi label="Cuentas bloqueadas (24 h)" value={d.blockedLogins} tone={d.blockedLogins > 0 ? 'red' : undefined} sub="5 fallos seguidos = 15 min de bloqueo" /></Col>
        <Col md={2} sm={4} xs={6}><Kpi label="Accesos denegados (24 h)" value={d.accessDenied} tone={d.accessDenied > 0 ? 'amber' : undefined} sub="Intentos de usar algo sin permiso" /></Col>
        <Col md={2} sm={4} xs={6}><Kpi label="Usuarios activos (24 h)" value={d.activeUsers} /></Col>
        <Col md={2} sm={4} xs={6}>
          <Kpi label="Cuentas por revisar" value={a.neverLoggedIn + a.mustChangePassword} tone={(a.neverLoggedIn + a.mustChangePassword) > 0 ? 'amber' : undefined}
            sub={`${a.neverLoggedIn} nunca entró · ${a.mustChangePassword} sin contraseña propia · ${a.deactivated} desactivada(s)`} />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col lg={7}>
          <Card className="shadow-sm p-3 h-100">
            <Card.Title style={{ fontSize: '1.05rem' }}>Cuentas y quién está activo</Card.Title>
            <Table hover responsive size="sm" className="mb-0 align-middle">
              <thead><tr><th>Usuario</th><th>Rol</th><th>Último acceso</th><th className="text-end">Acciones (7 d)</th><th>Estado</th></tr></thead>
              <tbody>
                {data.users.map(u => (
                  <tr key={u._id}>
                    <td>{u.username}</td>
                    <td><Badge bg={u.role === 'admin' ? 'warning' : 'secondary'} text={u.role === 'admin' ? 'dark' : undefined}>{u.role}</Badge></td>
                    <td className="small">{u.lastLoginAt ? dateTime(u.lastLoginAt) : <span className="text-muted">Nunca</span>}</td>
                    <td className="text-end">{u.actionsLast7Days}</td>
                    <td>
                      {u.active === false ? <Badge bg="secondary">Desactivado</Badge> : <Badge bg="success">Activo</Badge>}
                      {u.mustChangePassword && <Badge bg="warning" text="dark" className="ms-1">Contraseña temporal</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Col>
        <Col lg={5}>
          <ChartCard title="Actividad de los últimos 14 días" subtitle="Acciones registradas por día" empty={data.dailyActivity.length === 0}>
            <Bar data={daily} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { ticks: { precision: 0 } } } }} />
          </ChartCard>
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg={6}>
          <Card className="shadow-sm p-3 h-100">
            <Card.Title style={{ fontSize: '1.05rem' }}>Intentos de acceso fallidos recientes</Card.Title>
            <Table hover responsive size="sm" className="mb-0">
              <thead><tr><th>Cuándo</th><th>Nombre probado</th><th>IP</th><th>Motivo</th><th /></tr></thead>
              <tbody>
                {data.recentFailedLogins.map(r => (
                  <tr key={r._id}>
                    <td className="small">{dateTime(r.at)}</td>
                    <td>{r.username}</td>
                    <td className="small text-muted">{cleanIp(r.ip)}</td>
                    <td className="small">{r.details?.reason}</td>
                    <td className="text-end">{r.ip && <BlockButton ip={r.ip} usernames={[r.username].filter(Boolean)} blocked={r.blocked} />}</td>
                  </tr>
                ))}
                {data.recentFailedLogins.length === 0 && <tr><td colSpan={5} className="text-muted text-center">Ninguno registrado.</td></tr>}
              </tbody>
            </Table>
            {data.topFailedIps.length > 0 && (
              <div className="small mt-3">
                <div className="text-muted mb-1">Direcciones con más fallos (24 h)</div>
                {data.topFailedIps.map(i => (
                  <div key={i.ip} className="d-flex justify-content-between align-items-center gap-2 py-1">
                    <span><strong>{cleanIp(i.ip)}</strong> — {i.attempts} intento(s) con: {i.usernames.join(', ')}</span>
                    <BlockButton ip={i.ip} usernames={i.usernames} blocked={i.blocked} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="shadow-sm p-3 h-100">
            <Card.Title style={{ fontSize: '1.05rem' }}>Cambios sensibles recientes</Card.Title>
            <div className="small text-muted mb-2">Usuarios, contraseñas, bloqueos y exportaciones del registro</div>
            <Table hover responsive size="sm" className="mb-0">
              <thead><tr><th>Cuándo</th><th>Quién</th><th>Qué</th></tr></thead>
              <tbody>
                {data.sensitiveEvents.map(r => (
                  <tr key={r._id}>
                    <td className="small">{dateTime(r.at)}</td>
                    <td>{r.username || '—'}</td>
                    <td className="small">{r.summary}{!r.ok && <Badge bg="danger" className="ms-1">Falló</Badge>}</td>
                  </tr>
                ))}
                {data.sensitiveEvents.length === 0 && <tr><td colSpan={3} className="text-muted text-center">Sin eventos.</td></tr>}
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-0">
        <Col>
          <Card className="shadow-sm p-3">
            <Card.Title style={{ fontSize: '1.05rem' }}>Direcciones IP bloqueadas</Card.Title>
            <div className="small text-muted mb-2">Desde estas direcciones no se puede entrar a nada, ni siquiera con una sesión abierta. Se aplica en segundos.</div>
            <Table hover responsive size="sm" className="mb-0 align-middle">
              <thead><tr><th>IP</th><th>Motivo</th><th>Bloqueada por</th><th>Cuándo</th><th /></tr></thead>
              <tbody>
                {data.blockedIps.map(b => (
                  <tr key={b._id}>
                    <td><strong>{b.ip}</strong></td>
                    <td className="small">{b.reason || <span className="text-muted">—</span>}</td>
                    <td>{b.blockedBy}</td>
                    <td className="small">{dateTime(b.createdAt)}</td>
                    <td className="text-end"><Button size="sm" variant="outline-secondary" onClick={() => unblock(b.ip)}>Desbloquear</Button></td>
                  </tr>
                ))}
                {data.blockedIps.length === 0 && <tr><td colSpan={5} className="text-muted text-center">Ninguna IP bloqueada.</td></tr>}
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>

      <Modal show={!!target} onHide={closeBlock} centered>
        <Modal.Header closeButton><Modal.Title>Bloquear dirección IP</Modal.Title></Modal.Header>
        <Modal.Body>
          {target && (
            <>
              <p>Se bloqueará <strong>{target.ip}</strong> en todo el sistema. Podrás desbloquearla desde esta misma pantalla.</p>
              {known && (
                <div className="alert alert-warning">
                  Ojo: desde esta IP han iniciado sesión con éxito <strong>{known.join(', ')}</strong> en los últimos 90 días. Puede ser una persona de tu equipo o una oficina compartida.
                </div>
              )}
              <Form.Label className="small text-muted mb-1">Motivo (opcional)</Form.Label>
              <Form.Control value={reason} maxLength={200} onChange={e => setReason(e.target.value)} placeholder="Ej.: intentos de adivinar contraseñas" />
              {blockError && <div className="alert alert-danger mt-3 mb-0">{blockError}</div>}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeBlock}>Cancelar</Button>
          <Button variant="danger" disabled={busy} onClick={() => block(!!known)}>{known ? 'Bloquear de todos modos' : 'Bloquear'}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdminSecurityTab;
